import { CalendarModel } from '@bryntum/schedulerpro';
import { Dictionary } from 'lodash';
import { Company, Role, Team } from 'api/models';
import { Project } from 'api/project';
import { ResourceType } from 'api/resource-management/types';
import { StatusFieldOption } from 'api/tasks';
import { sortByDates } from 'utils/sort-by-dates';
import { sortByText } from 'utils/sort-by-text';
import {
  ProjectAsLeafResource,
  ProjectAsResource,
  RoleAsResource,
  SchedulerResourceType,
  TeamAsLeafResource,
  TeamsAsResource,
  TeamsWithProjectAsLeafResource,
  TeamWithProjectGroupedByRoles,
  UserAsLeafResource,
  UserAsResource,
  CompanyAsResource,
  PartnerHeader,
} from '../resource-types';
import {
  ExtendedUserIdentifier,
  ProjectUser,
  ProjectWithUserGroupedByRole,
  ProjectWithUserGroupedByRoleOptions,
  TeamsGroupedByCompany,
  TeamWithProject,
  TeamWithProjectGroupedByRoleLookup,
  UserAsResourceOptions,
  UsersGroupedByCompany,
  UserWithProjects,
} from '../types';
import { getTeamsWithProjectGroupedByRole } from './associated-data';
import { concat } from './common';
import {
  resourceIdForAssignProject,
  resourceIdForAssignUserOrTeam,
  resourceIdForExpandableRole,
  resourceIdForProject,
  resourceIdForProjectAsLeaf,
  resourceIdForRole,
  resourceIdForTeamAsLeaf,
  resourceIdForTeamHeader,
  resourceIdForUser,
  resourceIdForUserAsLeaf,
  resourceIdForCompany,
  resourceIdForPartnerHeader,
} from './id';

export const projectDateSorter = <
  T extends Pick<Project | ProjectWithUserGroupedByRole, 'startDate'>,
>(
  { startDate: startDateA }: T,
  { startDate: startDateB }: T,
): number => {
  if (startDateA == null) {
    return 1;
  }
  if (startDateB == null) {
    return -1;
  }

  return sortByDates(startDateA, startDateB);
};
export const projectNameSorter = <
  T extends Pick<Project | ProjectWithUserGroupedByRole, 'projectName'>,
>(
  { projectName: projectNameA }: T,
  { projectName: projectNameB }: T,
): 0 | 1 | -1 => sortByText(projectNameA, projectNameB);

export const projectAsLeafResource = ({
  project,
  userId,
  roleId,
  referenceTeam,
}: {
  project: Project;
  userId: number;
  roleId?: number;
  referenceTeam?: Team;
}): ProjectAsLeafResource => ({
  type: SchedulerResourceType.Project,
  id: resourceIdForProjectAsLeaf({
    projectId: project.projectId,
    userId,
    roleId,
    referenceTeamId: referenceTeam?.teamId,
  }),
  userId,
  roleId,
  referenceTeam,
  projectId: project.projectId,
  projectName: project.projectName,
  customer: project.customer,
  isUserProjectOwner: project.projectOwner.userId === userId,
  calendar: '24h' as unknown as CalendarModel,
  autoAllocation: project.autoAllocation,
});

export const teamAsResource = ({
  projectId,
  projectName,
  teamId,
  teamName,
  roleId,
  customer,
  autoAllocation,
}: TeamWithProject): TeamsWithProjectAsLeafResource => ({
  type: SchedulerResourceType.Team,
  id: resourceIdForTeamAsLeaf(projectId, teamId),
  projectId,
  projectName,
  teamId,
  roleId,
  teamName,
  customer,
  calendar: '24h' as unknown as CalendarModel,
  autoAllocation,
});

export const expandableRoleAsResource = (
  { roleId, roleName }: Role,
  teamsWithAdditionalInfo: TeamWithProjectGroupedByRoleLookup,
  companyId?: number,
): TeamWithProjectGroupedByRoles => ({
  type: SchedulerResourceType.ExpandableRole,
  id: resourceIdForExpandableRole(roleId, companyId),
  companyId,
  roleName,
  roleId,
  teamCount: teamsWithAdditionalInfo[roleId].length,
  calendar: '24h' as unknown as CalendarModel,
  children: teamsWithAdditionalInfo[roleId].map(teamAsResource),
});

export const teamsWithProjectGroupedByRoleAsResource = (
  teamsWithAdditionalInfo: TeamWithProjectGroupedByRoleLookup,
  roleLookup: Dictionary<Role>,
): TeamsAsResource => ({
  type: SchedulerResourceType.TeamHeader,
  id: resourceIdForTeamHeader(),
  children: Object.keys(teamsWithAdditionalInfo).map((roleId) =>
    expandableRoleAsResource(roleLookup[roleId], teamsWithAdditionalInfo),
  ),
});

export const userAsResource = (
  {
    user: { userId, firstName, lastName, photoUrl, role, company, userType },
    projects,
  }: UserWithProjects,
  { includeAssignProject }: UserAsResourceOptions,
): UserAsResource => {
  return {
    type: SchedulerResourceType.User,
    id: resourceIdForUser(userId),
    userId,
    firstName,
    lastName,
    photoUrl,
    company,
    userType,
    role,
    calendar: '24h' as unknown as CalendarModel,
    children: concat(
      projects.map(
        ({ project, role: memberRole, referenceTeam }) =>
          project &&
          projectAsLeafResource({
            project,
            userId,
            roleId: memberRole?.roleId,
            referenceTeam,
          }),
      ),
      includeAssignProject
        ? [
            {
              type: SchedulerResourceType.AssignProject,
              id: resourceIdForAssignProject(ResourceType.User, userId),
              userId,
              userType,
              company,
              alreadyAssignedProjectIds: projects.map(
                ({ project }) => project && project.projectId,
              ),
            },
          ]
        : [],
    ),
  };
};

export const convertUsersToResourceTree = (
  users: UserWithProjects[],
  options: UserAsResourceOptions,
): UserAsResource[] => users.map((user) => userAsResource(user, options));

export const userAsLeafResource = ({
  projectId,
  resource,
  totalAllocation = 0,
  projectOwnerId,
  autoAllocation,
}: {
  projectId: number;
  resource: ExtendedUserIdentifier;
  totalAllocation: number;
  projectOwnerId?: number;
  autoAllocation?: boolean;
}): UserAsLeafResource => {
  const {
    user: { userId, role, firstName, lastName, photoUrl, company, userType },
    role: memberRole,
    referenceTeam,
  } = resource;

  return {
    type: SchedulerResourceType.User,
    id: resourceIdForUserAsLeaf({
      projectId,
      userId,
      roleId: memberRole?.roleId,
      referenceTeamId: referenceTeam?.teamId,
    }),
    userId,
    roleId: memberRole?.roleId,
    referenceTeam,
    firstName,
    lastName,
    photoUrl,
    role,
    company,
    userType,
    projectId,
    isProjectOwner: projectOwnerId === userId,
    totalAllocation,
    calendar: '24h' as unknown as CalendarModel,
    autoAllocation,
  };
};
export const teamAsLeafResource = (
  placeholder: Team,
  parentProjectId: number,
  totalAllocation = 0,
  autoAllocation?: boolean,
): TeamAsLeafResource => ({
  type: SchedulerResourceType.Team,
  id: resourceIdForTeamAsLeaf(parentProjectId, placeholder.teamId),
  teamId: placeholder.teamId,
  teamName: placeholder.teamName,
  roleId: placeholder.roleId,
  userList: placeholder.userList,
  companyList: placeholder.companyList,
  projectId: parentProjectId,
  totalAllocation,
  calendar: '24h' as unknown as CalendarModel,
  autoAllocation,
});
export const roleAsResource = (
  projectId: number,
  { roleId, roleName }: Role,
): RoleAsResource<UserAsLeafResource | TeamAsLeafResource> => ({
  type: SchedulerResourceType.Role,
  id: resourceIdForRole(projectId, roleId),
  roleId,
  roleName,
  children: [],
});

const getUsersAndTeamsAsResource = (
  projectMembers: ProjectUser[],
  teams: Team[],
  projectId: number,
  projectOwnerId: number,
  showTeams: boolean,
  autoAllocation?: boolean,
): (UserAsLeafResource | TeamAsLeafResource)[] => [
  ...(projectMembers.map(({ user, role, referenceTeam }) =>
    userAsLeafResource({
      projectId,
      resource: {
        type: ResourceType.User,
        user,
        role,
        referenceTeam,
      },
      totalAllocation: 0,
      projectOwnerId,
      autoAllocation,
    }),
  ) ?? []),
  ...(showTeams
    ? teams.map((team) =>
        teamAsLeafResource(team, projectId, undefined, autoAllocation),
      ) ?? []
    : []),
];

export const projectAsResource = (
  {
    projectId,
    projectName,
    customer,
    budgetedHours,
    projectOwner,
    fields,
    startDate,
    autoAllocation,
  }: Project,
  statusFieldId?: number,
) => ({
  type: SchedulerResourceType.Project,
  id: resourceIdForProject(projectId),
  projectId,
  projectName,
  customer,
  projectOwner,
  fields,
  startDate,
  budgetedHours: budgetedHours != null ? budgetedHours * 60 : budgetedHours,
  totalAllocation: 0,
  resourceMargin: 0,
  autoAllocation,
  status:
    statusFieldId != null
      ? (fields.find((x) => x.fieldId === statusFieldId)
          ?.metaFieldValue as StatusFieldOption)
      : undefined,
  calendar: '24h' as unknown as CalendarModel,
});

export const convertProjectsToResourceTree = (
  projects: ProjectWithUserGroupedByRole[],
  {
    includeAssignUserOrPlaceholder,
    showTeams,
  }: ProjectWithUserGroupedByRoleOptions,
  statusFieldId?: number,
): ProjectAsResource[] => {
  return projects.map(
    ({
      projectId,
      projectName,
      customer,
      budgetedHours,
      teamMembersByRoles,
      projectOwner,
      fields,
      startDate,
      autoAllocation,
    }) => ({
      type: SchedulerResourceType.Project,
      id: resourceIdForProject(projectId),
      projectId,
      projectName,
      customer,
      startDate,
      budgetedHours: budgetedHours != null ? budgetedHours * 60 : budgetedHours,
      dataLoading: true,
      totalAllocation: 0,
      resourceMargin: 0,
      status:
        statusFieldId != null
          ? (fields.find((x) => x.fieldId === statusFieldId)
              ?.metaFieldValue as StatusFieldOption)
          : undefined,
      calendar: '24h' as unknown as CalendarModel,
      autoAllocation,
      children: concat(
        teamMembersByRoles
          .filter(
            ({ projectMembers, teams }) =>
              projectMembers.length > 0 || teams.length > 0,
          )
          .map(({ roleId, roleName, projectMembers, teams }) => ({
            type: SchedulerResourceType.Role,
            id: resourceIdForRole(projectId, roleId),
            roleId,
            roleName,
            children: getUsersAndTeamsAsResource(
              projectMembers,
              teams,
              projectId,
              projectOwner.userId,
              showTeams,
              autoAllocation,
            ),
          })),
        includeAssignUserOrPlaceholder
          ? [
              {
                type: SchedulerResourceType.AssignUserOrPlaceholder,
                id: resourceIdForAssignUserOrTeam(projectId),
                projectId,
              },
            ]
          : [],
      ),
    }),
  );
};

export const convertUserGroupedByCompanyToResourceTree = ({
  companies,
  userIdsGroupedByCompany,
  usersWithProjects,
  userResourceOptions,
  teamIdsGroupedByCompany,
  projectLookup,
  roles,
  roleLookup,
}: {
  companies: Company[];
  userIdsGroupedByCompany: UsersGroupedByCompany;
  usersWithProjects: Dictionary<UserWithProjects>;
  roles: Role[];
  roleLookup: Dictionary<Role>;
  projectLookup: Dictionary<Project>;
  teamIdsGroupedByCompany: TeamsGroupedByCompany;
  userResourceOptions: UserAsResourceOptions;
}): PartnerHeader => {
  const companyResources = companies.map((company) => {
    const usersWithProjectForCompany = (
      userIdsGroupedByCompany[company.companyId] ?? []
    )
      .map((userId) => usersWithProjects[userId])
      .filter(Boolean);
    const userResources = convertUsersToResourceTree(
      usersWithProjectForCompany,
      userResourceOptions,
    );
    const teamsWithProject = (
      teamIdsGroupedByCompany[company.companyId] ?? []
    ).map((team) =>
      team.projectId != null
        ? {
            ...projectLookup[team.projectId],
            ...team,
          }
        : team,
    ) as TeamWithProject[];
    const teamsGroupedByRole = getTeamsWithProjectGroupedByRole(
      teamsWithProject,
      roles,
    );
    const teamResources = Object.keys(teamsGroupedByRole).map((roleId) =>
      expandableRoleAsResource(
        roleLookup[roleId],
        teamsGroupedByRole,
        company.companyId,
      ),
    );

    return {
      type: SchedulerResourceType.Company,
      id: resourceIdForCompany(company.companyId),
      companyId: company.companyId,
      companyName: company.companyName,
      companyLogoUrl: company.companyLogoUrl,
      children: concat(userResources, teamResources),
    } as CompanyAsResource;
  });

  return {
    type: SchedulerResourceType.PartnerHeader,
    id: resourceIdForPartnerHeader(),
    children: companyResources,
  };
};
